import React, { useState } from "react";
import { usePageContent } from "customProviders/LocationProvider";
import { useHeaderAtom } from "hooks/atoms/useHeaderAtom";
import { useLocalization } from "hooks/useLocalization";
import useUserPreferences from "hooks/useUserPreferences";
import { useAppId } from "hooks/useAppId/useAppId";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  Typography
} from "unstyled-ui-components";
import {
  PrimaryColumn,
  SecondaryColumn,
  TertiaryColumn
} from "../WORHMenuContentV2";
import RHDropdownIconV2 from "icons/RHDropdownIconV2";
import parsedCatalogNavigation, {
  groupChildCategories
} from "utils/parseCatalogNavigationData";
import useGetNavigation from "hooks/useGetNavigation";
import BrandLogo from "../BrandLogo";
import RHCloseIcon from "icons/RHCloseIcon";
import useMediaQueryValues from "@RHCommerceDev/hooks/useMediaQueryValues/useMediaQueryValues";

const HamburgerMobileDrawer = () => {
  const locale = useLocalization();
  const { pageContent: pageContentObj } = usePageContent();
  const { mdDown } = useMediaQueryValues();
  const { isConcierge } = useAppId();
  const pageContent = (pageContentObj?.hamburgerNav as Record<string, unknown>)
    ?.childCategories as Record<string, unknown>[];

  const { resetAll } = useHeaderAtom();

  const { country: selectedCountry, language: selectedLanguage } =
    useUserPreferences();

  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    isConcierge
  );

  const [selectedItem, setSelectedItem] = useState<{
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  }>({
    sectionIndex: -1,
    primaryIndex: -1,
    secondaryIndex: -1
  });

  const sections = [navigationData];
  sections.push(...pageContent);

  const [isPrimaryDrawerOpen, setIsPrimaryDrawerOpen] = useState(true);
  const [isSecondaryDrawerOpen, setIsSecondaryDrawerOpen] = useState(false);
  const [isTertiaryDrawerOpen, setIsTertiaryDrawerOpen] = useState(false);

  const [isCountrySelectorOpen, setIsCountrySelectorOpen] =
    useState<boolean>(false);

  const selectedPrimary =
    sections?.[selectedItem.sectionIndex]?.childCategories?.[
      selectedItem.primaryIndex
    ];
  const selectedSecondary =
    selectedPrimary?.childCategories?.[selectedItem.secondaryIndex];

  const groupedCategories = groupChildCategories(
    selectedSecondary?.childCategories
  );

  const handlePrimaryClick = (sectionIndex: number, primaryIndex: number) => {
    setSelectedItem({ sectionIndex, primaryIndex, secondaryIndex: -1 });
    if (mdDown) {
      setIsTertiaryDrawerOpen(false); // Reset deeper drawer
      setIsSecondaryDrawerOpen(true);
    }
  };

  const handleSecondaryClick = (secondaryIndex: number) => {
    setSelectedItem(prev => ({ ...prev, secondaryIndex }));
    if (mdDown) setIsTertiaryDrawerOpen(true);
  };

  const DrawerHeader = () => {
    return (
      <DrawerTitle
        icon={
          <DrawerClose className="p-[3px] pb-3 sm:p-[5px]">
            <RHCloseIcon className="h-4 w-4 text-white" />
          </DrawerClose>
        }
        onClick={() => {
          resetAll();
        }}
        className="flex flex-row-reverse items-center bg-rh-mocha"
      >
        <div className="mx-auto flex h-[84px] pr-6 pt-3.5 text-white sm:h-[102px]">
          <BrandLogo />
        </div>
      </DrawerTitle>
    );
  };

  return (
    <>
      <Drawer open={isPrimaryDrawerOpen} onOpenChange={setIsPrimaryDrawerOpen}>
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent
            direction="left"
            className="w-full bg-rh-mocha px-4 text-white sm:px-8"
          >
            <DrawerHeader />
            <PrimaryColumn
              sections={sections}
              selectedItem={selectedItem}
              handlePrimaryClick={handlePrimaryClick}
              locale={locale}
              selectedCountry={selectedCountry}
              selectedLanguage={selectedLanguage}
              isCountrySelectorOpen={isCountrySelectorOpen}
              setIsCountrySelectorOpen={setIsCountrySelectorOpen}
              isConcierge={isConcierge}
              hamburgerMobileDrawer
            />
          </DrawerContent>
        </DrawerPortal>
      </Drawer>

      <Drawer
        open={isSecondaryDrawerOpen}
        onOpenChange={setIsSecondaryDrawerOpen}
      >
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent
            direction="left"
            className="w-full bg-rh-mocha  px-4 text-white sm:px-8"
          >
            <DrawerHeader />
            <DrawerTitle
              icon={
                <RHDropdownIconV2
                  className="mb-0.5 rotate-90 stroke-white text-white"
                  onClick={() => {
                    setIsSecondaryDrawerOpen(false);
                    setSelectedItem({
                      sectionIndex: -1,
                      primaryIndex: -1,
                      secondaryIndex: -1
                    });
                  }}
                />
              }
              className="flex flex-row-reverse items-center justify-end gap-4 bg-rh-mocha text-white"
            >
              {
                <Typography
                  variant="default"
                  fontFamily="rhc"
                  fontWeight="semibold"
                  className="text-base uppercase leading-[3.125em] tracking-[0.01em]"
                >
                  {selectedPrimary?.displayName}
                </Typography>
              }
            </DrawerTitle>
            <SecondaryColumn
              items={selectedPrimary?.childCategories ?? undefined}
              selectedItem={selectedItem}
              handleSecondaryClick={handleSecondaryClick}
              locale={locale}
              isCountrySelectorOpen={isCountrySelectorOpen}
              isConcierge={isConcierge}
              primaryCategory={selectedPrimary?.displayName || ""}
              hamburgerMobileDrawer
            />
          </DrawerContent>
        </DrawerPortal>
      </Drawer>

      {/* Tertiary Column Drawer */}
      <Drawer
        open={isTertiaryDrawerOpen}
        onOpenChange={setIsTertiaryDrawerOpen}
      >
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerContent
            direction="left"
            className="w-full bg-rh-mocha  px-4 text-white sm:px-8"
          >
            <DrawerHeader />
            <DrawerTitle
              icon={
                <div className="p-2 pr-4">
                  <RHDropdownIconV2
                    className="mb-0.5 rotate-90 stroke-white text-white "
                    onClick={() => {
                      setIsTertiaryDrawerOpen(false);
                      setSelectedItem(prev => ({
                        ...prev,
                        secondaryIndex: -1
                      }));
                    }}
                  />
                </div>
              }
              className="flex flex-row-reverse items-center justify-end bg-rh-mocha text-white"
            >
              {
                <div className="flex flex-row-reverse gap-[6px]">
                  <Typography
                    variant="default"
                    fontFamily="rhc"
                    fontWeight="semibold"
                    className="text-base uppercase leading-[3.125em] tracking-[0.01em]"
                  >
                    {selectedSecondary?.displayName}
                  </Typography>
                  <Typography
                    variant="default"
                    fontFamily="rhc"
                    fontWeight="semibold"
                    className="text-base uppercase leading-[3.125em] tracking-[0.01em]"
                  >
                    {"|"}
                  </Typography>
                  <Typography
                    variant="default"
                    fontFamily="rhc"
                    fontWeight="thin"
                    className="text-base uppercase leading-[3.125em] tracking-[0.01em]"
                  >
                    {selectedPrimary?.displayName}
                  </Typography>
                </div>
              }
            </DrawerTitle>
            <TertiaryColumn
              groups={groupedCategories}
              locale={locale}
              isConcierge={isConcierge}
              secondaryCategory={selectedSecondary?.displayName || ""}
              primaryCategory={selectedPrimary?.displayName || ""}
              hamburgerMobileDrawer
            />
          </DrawerContent>
        </DrawerPortal>
      </Drawer>
    </>
  );
};

export default HamburgerMobileDrawer;
